/**
 * Catalogue of the tile cases a style needs: the cases its levels contain, the cases that random
 * terrain built from the supported slopes produces (ranked by how often), and optionally every case
 * the shape classes can form at all. A case and its mirror image are separate rows.
 */
import type { Vec2 } from '../model/format.ts';
import { caseKey, enumerateCases, generalCase, type CaseLayer, type Directions } from './cases.ts';
import { NB } from './keys.ts';
import { classifyCells, createGrid, paintRings, ringsBounds, shapeKindAllowed } from './raster.ts';
import { EMPTY } from './shapes.ts';
import type { TerrainStyleData } from './types.ts';

export type CaseTier = 'levels' | 'common' | 'all';

export interface CaseRow {
  key: string;
  /** a neighbourhood producing the case in this orientation */
  own: number;
  nb: number[];
  /** cells of the learned levels showing the case */
  levels: number;
  /** cells of the random terrain sample showing it */
  common: number;
  /** share of the random terrain's cells with a case that show this one */
  share: number;
  /** share of all random-terrain cells covered by this case and the more common ones */
  cumulative: number;
}

const COMMON_SHARE = 0.99;

// ------------------------------------------------------------------ random terrain sample

function rng(seed: number) {
  let s = seed >>> 0;
  return () => ((s = (Math.imul(s, 1103515245) + 12345) >>> 0) / 4294967296);
}

/** Random terrain outlines using only the given slopes (plus straight edges and vertical steps). */
function randomOutlines(dirs: Directions, count: number, seed = 12345): Vec2[][] {
  const rnd = rng(seed);
  const pick = <T,>(a: T[]) => a[Math.floor(rnd() * a.length)];
  const steps: [number, number][] = [[1, 0]];
  if (dirs.s45) steps.push([1, 1], [1, -1]);
  if (dirs.s21) steps.push([2, 1], [2, -1]);
  if (dirs.s12) steps.push([1, 2], [1, -2]);
  // a quarter turn swaps 2:1 and 1:2 edges
  const turns = dirs.s21 === dirs.s12 ? [0, 1, 2, 3] : [0, 2];
  const profile = (W: number, y0: number, lo: number, hi: number): Vec2[] => {
    const pts: Vec2[] = [[0, y0]];
    let x = 0, y = y0;
    for (let guard = 0; x < W && guard < 400; guard++) {
      if (rnd() < 0.15) {
        const ny = Math.max(lo, Math.min(hi, y + pick([-3, -2, -1, 1, 2, 3])));
        if (ny !== y) pts.push([x, (y = ny)]);
        continue;
      }
      const [dx, dy] = pick(steps);
      const n = 1 + Math.floor(rnd() * 3);
      const nx = x + dx * n, ny = y + dy * n;
      if (ny < lo || ny > hi || nx > W) continue;
      pts.push([(x = nx), (y = ny)]);
    }
    if (x < W) pts.push([W, y]);
    return pts;
  };
  const out: Vec2[][] = [];
  for (let p = 0; p < count; p++) {
    const W = 16 + Math.floor(rnd() * 16);
    const top = profile(W, 10, 6, 16);
    const bottom = profile(W, rnd() < 0.4 ? 7 : 2, 0, 9).reverse();
    const rot = pick(turns);
    out.push([...top, ...bottom].map(([x, y]) => (rot === 0 ? [x, y] : rot === 1 ? [-y, x] : rot === 2 ? [-x, -y] : [y, -x]) as Vec2));
  }
  return out;
}

interface Sample {
  counts: Map<string, number>;
  rep: Map<string, { own: number; nb: number[] }>;
  total: number;
}

const sampleCache = new Map<string, Sample>();

function randomSample(layer: CaseLayer, dirs: Directions): Sample {
  const ck = `${layer}|${+dirs.s45}${+dirs.s21}${+dirs.s12}`;
  let s = sampleCache.get(ck);
  if (s) return s;
  s = { counts: new Map(), rep: new Map(), total: 0 };
  const nb = new Array<number>(8);
  for (const ring of randomOutlines(dirs, 600)) {
    const [x0, y0, x1, y1] = ringsBounds([ring]);
    const g = createGrid(Math.floor(x0) - 2, Math.floor(y0) - 2, Math.ceil(x1 - x0) + 5, Math.ceil(y1 - y0) + 5, 4);
    paintRings(g, [ring], 0);
    const cls = classifyCells(g, (o) => o === 0, (id) => shapeKindAllowed(id, dirs));
    const bad = new Set(cls.bad);
    const at = (x: number, y: number) => (x < 0 || y < 0 || x >= g.w || y >= g.h ? EMPTY : cls.shape[y * g.w + x]);
    for (let y = 0; y < g.h; y++)
      for (let x = 0; x < g.w; x++) {
        const own = at(x, y);
        if (own === EMPTY) continue;
        let skip = bad.has(y * g.w + x);
        for (let i = 0; i < 8 && !skip; i++) {
          const nx = x + NB[i][0], ny = y + NB[i][1];
          nb[i] = at(nx, ny);
          if (bad.has(ny * g.w + nx)) skip = true;
        }
        if (skip) continue;
        const k = caseKey(layer, own, nb);
        if (!k) continue;
        s.total++;
        s.counts.set(k, (s.counts.get(k) ?? 0) + 1);
        if (!s.rep.has(k)) s.rep.set(k, { own, nb: [...nb] });
      }
  }
  sampleCache.set(ck, s);
  return s;
}

// ------------------------------------------------------------------ catalogue

/** Cases seen in the levels the style was learned from (exact 3×3 contexts of the level rules). */
function levelCases(d: TerrainStyleData, layer: CaseLayer) {
  const counts = new Map<string, number>();
  const rep = new Map<string, { own: number; nb: number[] }>();
  for (const [k, arr] of Object.entries(d.level.rules)) {
    if (k[0] !== 'a' || k.length !== 10) continue;
    const own = k.charCodeAt(1) - 97;
    const nb = [...k.slice(2)].map((c) => c.charCodeAt(0) - 97);
    const key = caseKey(layer, own, nb);
    if (!key) continue;
    let n = 0;
    for (let i = 1; i < arr.length; i += 2) n += arr[i];
    counts.set(key, (counts.get(key) ?? 0) + n);
    if (!rep.has(key)) rep.set(key, { own, nb });
  }
  return { counts, rep };
}

/** share of random terrain from which an inside corner's corner shapes get their own rows in 'common' */
const DETAIL_SHARE = 0.001;

/**
 * Rows of the case catalogue, most needed first: cases from the levels, then the cases making up 99% of
 * random terrain with these slopes ('common'), then (tier 'all') every other possible case. Inside
 * corners come as their general case plus the corner shapes seen in the levels or (common) not rare.
 */
export function caseCatalog(d: TerrainStyleData, dirs: Directions, layer: CaseLayer, tier: CaseTier): CaseRow[] {
  const lv = levelCases(d, layer);
  const rows = new Map<string, CaseRow>();
  const add = (key: string, rep: { own: number; nb: number[] }) => {
    let r = rows.get(key);
    if (!r) rows.set(key, (r = { key, own: rep.own, nb: rep.nb, levels: 0, common: 0, share: 0, cumulative: 1 }));
    return r;
  };
  for (const [k, n] of [...lv.counts].sort((a, b) => b[1] - a[1])) {
    add(k, lv.rep.get(k)!).levels = n;
    const g = generalCase(k);
    if (g) add(g, lv.rep.get(k)!).levels += n;
  }
  if (tier !== 'levels') {
    const s = randomSample(layer, dirs);
    const total = Math.max(1, s.total);
    const groups = new Map<string, { n: number; keys: [string, number][] }>();
    for (const [k, n] of [...s.counts].sort((a, b) => b[1] - a[1])) {
      const g = generalCase(k) ?? k;
      let e = groups.get(g);
      if (!e) groups.set(g, (e = { n: 0, keys: [] }));
      e.n += n;
      e.keys.push([k, n]);
    }
    let acc = 0;
    for (const [g, e] of [...groups].sort((a, b) => b[1].n - a[1].n)) {
      const before = acc / total;
      acc += e.n;
      // 'common': the cases making up 99% of the random terrain's cells
      if (tier === 'common' && before >= COMMON_SHARE && !rows.has(g)) continue;
      const gr = add(g, s.rep.get(e.keys[0][0])!);
      gr.common = e.n;
      gr.share = e.n / total;
      gr.cumulative = acc / total;
      if (e.keys[0][0] === g) continue;
      for (const [k, n] of e.keys) {
        if (tier === 'common' && n / total < DETAIL_SHARE && !rows.has(k)) continue;
        const r = add(k, s.rep.get(k)!);
        r.common = n;
        r.share = n / total;
        r.cumulative = gr.cumulative;
      }
    }
  }
  if (tier === 'all')
    for (const e of enumerateCases(layer, dirs)) if (!rows.has(e.key)) add(e.key, { own: e.key.charCodeAt(1) - 97, nb: e.nb });
  return [...rows.values()].sort((a, b) => b.levels - a.levels || b.common - a.common || a.key.localeCompare(b.key));
}
